import React, { useEffect, useRef, useState } from 'react'
import {
  buildingTypeForTrustedBuilding,
  initialBuildingUnits,
  outlineHasOtherUnits,
  pickReferenceCandidate,
  resolveBlockOfFlatsReference,
  resolveBuildingUnitUprns
} from './blockOfFlatsReference'
import {
  EMPTY_MAP_SELECTION,
  ENTRY_MODE_MANUAL,
  ENTRY_MODE_MAP,
  initialEntryMode,
  manualEntryBpPatch,
  manualEntryValues
} from './buildingEntryMode'
import BuildingEntryChoice from './BuildingEntryChoice'
import ManualBuildingEntry from './ManualBuildingEntry'
import Panel from 'features/createBp/components/atoms/Panel'
import { useTranslation } from 'react-i18next'
import { validateBuildingInformationWithoutUprnValues } from 'features/createBp/ducks/schema'
import styled from 'styled-components'
import { propOr, equals } from 'ramda'
import { useDispatch, useSelector } from 'react-redux'
import { getCreatedBpId } from 'features/createBp/ducks/selectors'
import { isNotNilOrEmpty } from 'utils/ramda'
import {
  updateBpRoutine,
  updateCreatedBpRoutine
} from 'features/createBp/ducks/actions'
import OsBuildingMultiSelectMap from 'features/createBp/components/OsBuildingMultiSelectMap'
import InputDescription from 'features/createBp/components/atoms/InputDescription'
import Checkbox from 'components/atoms/Checkbox'
import * as bpService from 'services/BpService'
import CircularProgress from '@mui/material/CircularProgress'
import Skeleton from '@mui/material/Skeleton'
import { getApiErrors } from 'utils/errors'
import Modal from 'components/atoms/Modal'
import ApartmentOutlinedIcon from '@mui/icons-material/ApartmentOutlined'
import CheckCircleOutlineOutlinedIcon from '@mui/icons-material/CheckCircleOutlineOutlined'
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined'
import SearchOutlinedIcon from '@mui/icons-material/SearchOutlined'

const BuildingInformation = ({
  completed,
  open,
  setActiveStep,
  setBp,
  setCompleted,
  bp
}) => {
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const bpId = useSelector(getCreatedBpId)
  const resolvedBpId = bpId || bp.id

  const [sectionValues, setSectionValues] = useState({
    name: propOr('', 'name', bp),
    uprn: propOr('', 'uprn', bp),
    postalCode: propOr('', 'postalCode', bp),
    street: propOr('', 'street', bp),
    city: propOr('', 'city', bp),
    expectedUprnAssignmentDate: propOr(null, 'expectedUprnAssignmentDate', bp),
    groundFloor: true,
    // Saved on the Building Passport and read back by unit sync: the sync
    // review then opens straight on the public-data outcome, so the official
    // OS unit names replace the generated defaults. No lookups run for it.
    usePublicDataUnitNames: Boolean(propOr(false, 'usePublicDataUnitNames', bp))
  })
  const [addressSearch, setAddressSearch] = useState(
    propOr('', 'postalCode', bp)
  )
  const [valid, setIsValid] = useState(false)
  const [referenceLookupLoading, setReferenceLookupLoading] = useState(false)
  const [referenceLookupMessage, setReferenceLookupMessage] = useState(null)
  const [selectionLocked, setSelectionLocked] = useState(
    Boolean(propOr('', 'uprn', bp))
  )
  const [lookupModal, setLookupModal] = useState({
    open: false,
    title: '',
    message: '',
    mode: null
  })
  const [estatePrompt, setEstatePrompt] = useState({
    open: false,
    mode: 'notice',
    reason: 'unavailable-selection'
  })

  const [manualEntryEnabled, setManualEntryEnabled] = useState(false)
  const [entryMode, setEntryMode] = useState(() => initialEntryMode(bp))
  const [primaryPickerOpen, setPrimaryPickerOpen] = useState(false)
  // Building UPRN that the postcode lookup proved owns the searched postcode's flats.
  const trustedBuildingRef = useRef({ uprn: '', childUprns: [] })
  // The OS outline can cover neighbouring buildings; these are the selected building's own units.
  const [buildingUnits, setBuildingUnits] = useState(() =>
    initialBuildingUnits(bp)
  )
  const [buildingUnitsLoading, setBuildingUnitsLoading] = useState(false)
  const primaryPickerRef = useRef(null)
  const [mapSelection, setMapSelection] = useState({
    toids: propOr([], 'selectedMapToids', bp),
    uprns: propOr([], 'selectedMapUprns', bp),
    features: propOr([], 'selectedMapFeatures', bp),
    links: propOr([], 'selectedOsLinkedIdentifiers', bp)
  })
  const [mapFocusRequest, setMapFocusRequest] = useState(null)
  const [mapSearchLoading, setMapSearchLoading] = useState(false)

  const currentSelectionTitle = mapSelection.toids.length
    ? `${mapSelection.toids.length} building${
        mapSelection.toids.length === 1 ? '' : 's'
      } selected`
    : 'No building selected'

  const handleOnChange = (name, value) =>
    setSectionValues(prev => ({ ...prev, [name]: value }))

  const setValues = (name, value) => {
    setReferenceLookupMessage(null)
    handleOnChange(name, value)
  }

  const closeLookupModal = () =>
    setLookupModal(prev => ({ ...prev, open: false }))

  const closeEstatePrompt = () =>
    setEstatePrompt(prev => ({ ...prev, open: false }))

  const normalizeInteger = value => {
    if (value === null || value === undefined || value === '') {
      return null
    }

    const parsed = Number(value)

    return Number.isInteger(parsed) ? parsed : null
  }

  const hasBuildingDataBasementPresence = value => {
    if (value === null || value === undefined || value === '') {
      return false
    }

    const normalizedValue = String(value).trim().toLowerCase()

    return ['true', 'yes', '1', 'present'].includes(normalizedValue)
  }

  const hasLookupAddressDetails = data =>
    Boolean(
      data?.name ||
        data?.street ||
        data?.address ||
        data?.city ||
        data?.postalCode
    )

  const inferSuggestedBuildingTypeFromCandidate = candidate => {
    if (!candidate) {
      return null
    }

    const classificationCode = String(candidate.classificationCode || '')
      .trim()
      .toUpperCase()
    const searchText = [
      candidate.name,
      candidate.street,
      candidate.address,
      candidate.postalCode
    ]
      .filter(Boolean)
      .join(' ')
      .toLowerCase()
    const contains = needles =>
      needles.some(needle => searchText.includes(needle))
    const looksLikeSingleDwelling = /^[0-9]+[a-z]?$/i.test(
      String(candidate.name || '').trim()
    )

    if (classificationCode.startsWith('CR')) return 'retail'
    if (classificationCode.startsWith('CO')) return 'office'
    if (classificationCode.startsWith('CL')) return 'leisure'
    if (classificationCode.startsWith('CH')) return 'hotel'
    if (
      classificationCode.startsWith('CI04') ||
      classificationCode.startsWith('CI05')
    )
      return 'storage'
    if (classificationCode.startsWith('CI')) return 'industrial'
    if (classificationCode.startsWith('CA')) return 'assembly'
    if (classificationCode.startsWith('RD'))
      return 'residential.dwelling.sole_unit'

    if (
      contains([
        'flat',
        'apartment',
        'maisonette',
        'duplex',
        'penthouse',
        'studio'
      ])
    ) {
      return 'residential'
    }

    if (contains(['office'])) return 'office'
    if (contains(['shop', 'retail', 'store'])) return 'retail'
    if (contains(['warehouse', 'storage', 'depot'])) return 'storage'
    if (contains(['factory', 'industrial', 'manufacturing', 'workshop']))
      return 'industrial'
    if (contains(['hotel'])) return 'hotel'
    if (contains(['leisure', 'cinema', 'theatre', 'hospitality']))
      return 'leisure'
    if (contains(['assembly', 'community hall', 'place of worship']))
      return 'assembly'
    if (contains(['recreation', 'sports', 'sport', 'gym'])) return 'recreation'
    if (
      contains(['bungalow', 'house', 'cottage', 'villa']) ||
      looksLikeSingleDwelling
    ) {
      return 'residential.dwelling.sole_unit'
    }

    return null
  }

  const applyCandidateFallback = candidate => {
    const fallbackSuggestedBuildingType =
      inferSuggestedBuildingTypeFromCandidate(candidate)
    const fallbackSuggestedUnitsCount =
      normalizeInteger(candidate?.suggestedUnitsCount) ??
      normalizeInteger(candidate?.addressesCount)

    setBp(prev => ({
      ...prev,
      referenceData: candidate?.classificationCode
        ? {
            ...(prev.referenceData || {}),
            classificationCode: candidate.classificationCode,
            classificationCodeDescription:
              candidate.classificationCodeDescription ||
              prev?.referenceData?.classificationCodeDescription ||
              null
          }
        : prev.referenceData,
      suggestedBuildingType:
        fallbackSuggestedBuildingType || prev.suggestedBuildingType,
      buildingTypes: fallbackSuggestedBuildingType
        ? [
            {
              type: fallbackSuggestedBuildingType,
              value: '0',
              units:
                fallbackSuggestedUnitsCount ??
                normalizeInteger(prev.suggestedUnitsCount) ??
                0,
              area: null,
              description: candidate?.classificationCodeDescription || ''
            }
          ]
        : prev.buildingTypes,
      suggestedUnitsCount:
        fallbackSuggestedUnitsCount ?? prev.suggestedUnitsCount
    }))

    return fallbackSuggestedBuildingType
  }

  //
  const fillBuildingDetailsFromLookup = (data, source = 'uprn') => {
    const classificationDescription =
      data?.referenceData?.classificationCodeDescription || ''
    const normalizedSuggestedUnitsCount = normalizeInteger(
      data?.suggestedUnitsCount
    )
    const normalizedFloorsNumber = normalizeInteger(data?.floorsNumber)
    const buildingDataIndicatesBasement = hasBuildingDataBasementPresence(
      data?.buildingData?.basementPresence
    )

    setSelectionLocked(true)
    setManualEntryEnabled(false)
    setSectionValues(prev => ({
      ...prev,
      uprn: data.uprn || prev.uprn,
      name: data.name || prev.name,
      street: data.street || data.address || prev.street,
      city: data.city || prev.city,
      postalCode: data.postalCode || prev.postalCode
    }))

    setBp(prev => ({
      ...prev,
      uprn: data.uprn || prev.uprn,
      name: data.name || prev.name,
      street: data.street || data.address || prev.street,
      city: data.city || prev.city,
      postalCode: data.postalCode || prev.postalCode,
      referenceData: data.referenceData || prev.referenceData,
      buildingData: data.buildingData || prev.buildingData,
      suggestedBuildingType:
        data.suggestedBuildingType || prev.suggestedBuildingType,
      suggestedUnitsCount:
        normalizedSuggestedUnitsCount ?? prev.suggestedUnitsCount,
      floorsNumber: normalizedFloorsNumber ?? prev.floorsNumber,
      undergroundFloorsNumber:
        normalizeInteger(prev.undergroundFloorsNumber) ??
        (buildingDataIndicatesBasement ? 1 : prev.undergroundFloorsNumber),
      buildingTypes: data.suggestedBuildingType
        ? [
            {
              type: data.suggestedBuildingType,
              value: '0',
              units: normalizedSuggestedUnitsCount ?? 1,
              area: null,
              description: classificationDescription
            }
          ]
        : prev.buildingTypes
    }))

    setReferenceLookupMessage({
      type: 'success',
      text: buildLookupSuccessMessage(source)
    })
  }

  const lookupBuildingDetails = async ({ uprn = '', seedCandidate = null }) => {
    const normalizedUprn = String(uprn || '').trim()
    const normalizedPostcode = String(
      seedCandidate?.postalCode ||
        sectionValues.postalCode ||
        bp?.postalCode ||
        ''
    ).trim()

    if (!normalizedUprn) return

    // A trusted building is looked up through one of its flats, whose postcode page is already cached.
    const trustedBuilding = trustedBuildingRef.current
    const isTrustedBuilding =
      Boolean(trustedBuilding.uprn) &&
      (normalizedUprn === trustedBuilding.uprn ||
        String(seedCandidate?.uprn || '') === trustedBuilding.uprn)
    const identityUprn = isTrustedBuilding ? trustedBuilding.uprn : normalizedUprn

    setReferenceLookupLoading(true)
    setReferenceLookupMessage(null)
    setManualEntryEnabled(false)

    if (seedCandidate) {
      const seedSuggestedUnitsCount =
        normalizeInteger(seedCandidate?.suggestedUnitsCount) ??
        normalizeInteger(seedCandidate?.addressesCount)

      setSelectionLocked(true)
      setSectionValues(prev => ({
        ...prev,
        uprn: seedCandidate.uprn || prev.uprn,
        name: seedCandidate.name || prev.name,
        street: seedCandidate.street || prev.street,
        city: seedCandidate.city || prev.city,
        postalCode: seedCandidate.postalCode || prev.postalCode
      }))
      setBp(prev => ({
        ...prev,
        uprn: seedCandidate.uprn || prev.uprn,
        name: seedCandidate.name || prev.name,
        street: seedCandidate.street || prev.street,
        city: seedCandidate.city || prev.city,
        postalCode: seedCandidate.postalCode || prev.postalCode,
        suggestedUnitsCount: seedSuggestedUnitsCount ?? prev.suggestedUnitsCount
      }))
    }

    try {
      const existingResults = await bpService.searchBps({
        query: identityUprn
      })
      const existingBp = (existingResults?.data?.data || []).find(
        item =>
          String(item.uprn || '') === identityUprn && item.id !== resolvedBpId
      )

      if (existingBp) {
        if (existingBp.status === 'draft') {
          const existingDraftResponse = await bpService.fetchBp(existingBp.id)
          const existingDraft = existingDraftResponse?.data?.data

          if (existingDraft) {
            let enrichedDraft = existingDraft

            try {
              const referenceLookupResponse =
                await bpService.lookupBuildingReference({
                  uprn: existingDraft.uprn || normalizedUprn,
                  postcode:
                    String(
                      existingDraft.postalCode ||
                        existingDraft.postal_code ||
                        normalizedPostcode
                    ).trim() || undefined
                })
              const referenceLookupData = referenceLookupResponse?.data?.data

              if (referenceLookupData) {
                const normalizedSuggestedUnitsCount = normalizeInteger(
                  referenceLookupData.suggestedUnitsCount
                )
                const normalizedFloorsNumber = normalizeInteger(
                  referenceLookupData.floorsNumber
                )
                const classificationDescription =
                  referenceLookupData?.referenceData
                    ?.classificationCodeDescription || ''
                const buildingDataIndicatesBasement =
                  hasBuildingDataBasementPresence(
                    referenceLookupData?.buildingData?.basementPresence
                  )

                enrichedDraft = {
                  ...existingDraft,
                  referenceData:
                    referenceLookupData.referenceData ||
                    existingDraft.referenceData,
                  buildingData:
                    referenceLookupData.buildingData ||
                    existingDraft.buildingData,
                  suggestedBuildingType:
                    referenceLookupData.suggestedBuildingType ||
                    existingDraft.suggestedBuildingType,
                  suggestedUnitsCount:
                    normalizedSuggestedUnitsCount ??
                    existingDraft.suggestedUnitsCount,
                  floorsNumber:
                    normalizedFloorsNumber ?? existingDraft.floorsNumber,
                  undergroundFloorsNumber:
                    normalizeInteger(existingDraft.undergroundFloorsNumber) ??
                    (buildingDataIndicatesBasement
                      ? 1
                      : existingDraft.undergroundFloorsNumber),
                  buildingTypes: referenceLookupData.suggestedBuildingType
                    ? [
                        {
                          type: referenceLookupData.suggestedBuildingType,
                          value: '0',
                          units: normalizedSuggestedUnitsCount ?? 1,
                          area: null,
                          description: classificationDescription
                        }
                      ]
                    : existingDraft.buildingTypes
                }
              }
            } catch (error) {
              // Keep the existing draft if the enrichment lookup fails.
            }

            dispatch(updateCreatedBpRoutine.success(enrichedDraft))
            setBp(prev => ({
              ...prev,
              ...enrichedDraft
            }))
            setSectionValues({
              name: enrichedDraft.name || '',
              uprn: enrichedDraft.uprn || normalizedUprn,
              postalCode: enrichedDraft.postalCode || '',
              street: enrichedDraft.street || '',
              city: enrichedDraft.city || '',
              expectedUprnAssignmentDate:
                enrichedDraft.expectedUprnAssignmentDate || null,
              groundFloor: enrichedDraft.groundFloor ?? true
            })
            setSelectionLocked(Boolean(enrichedDraft.uprn))
            setReferenceLookupMessage({
              type: 'success',
              text: `We found your existing draft "${enrichedDraft.name}". The wizard has been reconnected to it, so you can continue editing.`
            })
            return
          }
        }

        setLookupModal({
          open: true,
          title: 'Building Passport already exists',
          message: `This UPRN is already linked to the existing Building Passport "${existingBp.name}". Open the existing Building Passport instead of creating a new one.`,
          mode: 'warning'
        })
        setReferenceLookupMessage({
          type: 'warning',
          text: 'A Building Passport for this building already exists in the system, so it cannot be created again from this wizard.'
        })
        return
      }

      let lookupData = null

      const { data } = await bpService.lookupBuildingReference({
        uprn: normalizedUprn,
        postcode: seedCandidate ? undefined : normalizedPostcode || undefined
      })

      if (data?.data) {
        lookupData = data.data
      }

      // The building record itself can sit on another postcode than its flats.
      if (isTrustedBuilding && lookupData) {
        lookupData = {
          ...lookupData,
          uprn: identityUprn,
          postalCode: normalizedPostcode || lookupData.postalCode,
          suggestedUnitsCount:
            trustedBuilding.unitUprns?.length ||
            trustedBuilding.childUprns.length ||
            lookupData.suggestedUnitsCount,
          suggestedBuildingType: buildingTypeForTrustedBuilding(
            lookupData,
            trustedBuilding.childClassificationCode
          )
        }
      }

      if (
        !isTrustedBuilding &&
        seedCandidate &&
        normalizedPostcode &&
        lookupData?.postalCode &&
        normalizePostcode(lookupData.postalCode) !==
          normalizePostcode(normalizedPostcode)
      ) {
        setSelectionLocked(false)
        setManualEntryEnabled(false)
        setSectionValues(prev => ({
          ...prev,
          uprn: '',
          name: '',
          street: '',
          city: '',
          postalCode: normalizedPostcode
        }))
        setBp(prev => ({
          ...prev,
          uprn: '',
          name: '',
          street: '',
          city: '',
          postalCode: normalizedPostcode,
          referenceData: null,
          buildingData: null,
          suggestedBuildingType: null,
          floorsNumber: null,
          undergroundFloorsNumber: null,
          buildingTypes: []
        }))
        setReferenceLookupMessage({
          type: 'warning',
          text: `The selected building resolves to postcode ${lookupData.postalCode}, but you searched for ${normalizedPostcode}. Select a building inside ${normalizedPostcode}, or search for ${lookupData.postalCode} if this is the building you meant.`
        })
        return
      }

      if (!lookupData) {
        const fallbackSuggestedBuildingType = seedCandidate
          ? applyCandidateFallback(seedCandidate)
          : null

        if (seedCandidate) {
          setManualEntryEnabled(true)
        }

        setReferenceLookupMessage({
          type: fallbackSuggestedBuildingType ? 'success' : 'warning',
          text: seedCandidate
            ? fallbackSuggestedBuildingType
              ? 'The selected building has been filled in. Full reference details are not available for this UPRN, but we suggested a main building type from the selected candidate.'
              : 'The selected OS building is linked to UPRNs, but Postcoder did not return address details for those UPRNs. Complete the basic fields manually.'
            : 'We could not find extra building details for the selected building.'
        })
        return
      }

      fillBuildingDetailsFromLookup(
        lookupData,
        seedCandidate ? 'map' : 'postcode'
      )
    } catch (error) {
      setReferenceLookupMessage({
        type: 'error',
        text: seedCandidate
          ? 'The selected building was linked to UPRNs, but we could not load the extra reference details right now. Complete the basic fields manually.'
          : getApiErrors(error)?.trim() ||
            'Building lookup failed. Please try a different postcode.'
      })
      if (seedCandidate) {
        setManualEntryEnabled(true)
      }
    } finally {
      setReferenceLookupLoading(false)
    }
  }

  const buildSeedCandidate = (uprn, toid = '', linkedUprnsCount = null) => ({
    uprn,
    toid,
    name: sectionValues.name,
    street: sectionValues.street,
    city: sectionValues.city,
    postalCode: sectionValues.postalCode,
    address: sectionValues.street,
    suggestedUnitsCount: linkedUprnsCount ?? mapSelection.uprns.length,
    classificationCode: null,
    classificationCodeDescription: 'Selected from OS map'
  })

  const handlePrimaryUprnSelect = async uprn => {
    const normalizedUprn = String(uprn || '').trim()

    if (!normalizedUprn || referenceLookupLoading) {
      return
    }

    const keepsBuildingUnits = buildingUnits?.buildingUprn === normalizedUprn
    const unitUprns = keepsBuildingUnits ? buildingUnits.uprns : mapSelection.uprns

    if (!keepsBuildingUnits) {
      setBuildingUnits(null)
    }

    setBp(prev => ({
      ...prev,
      selectedMapUprns: unitUprns,
      suggestedUnitsCount: unitUprns.length || prev.suggestedUnitsCount
    }))

    const trustedBuilding = trustedBuildingRef.current
    const detailsUprn =
      trustedBuilding.uprn === normalizedUprn && trustedBuilding.childUprns.length
        ? trustedBuilding.childUprns[0]
        : normalizedUprn

    await lookupBuildingDetails({
      uprn: detailsUprn,
      seedCandidate: buildSeedCandidate(
        normalizedUprn,
        mapSelection.toids[0] || '',
        unitUprns.length
      )
    })
  }

  const loadBuildingUnits = async ({
    buildingUprn,
    name,
    street,
    city,
    postcodeChildUprns,
    mapUprns
  }) => {
    try {
      const { data } = await bpService.getBuildingUnitUprns({
        building_uprn: buildingUprn,
        name: name || undefined,
        street: street || undefined,
        city: city || undefined
      })
      const unitUprns = resolveBuildingUnitUprns({
        lookup: data,
        postcodeChildUprns,
        mapUprns
      })

      if (!unitUprns) {
        return null
      }

      trustedBuildingRef.current = {
        ...trustedBuildingRef.current,
        unitUprns
      }
      setBuildingUnits({ buildingUprn, uprns: unitUprns })
      setBp(prev => ({
        ...prev,
        selectedMapUprns: unitUprns,
        suggestedUnitsCount: unitUprns.length
      }))

      return unitUprns
    } catch (error) {
      // Keep the whole OS outline selection if the building's units cannot be confirmed.
      return null
    } finally {
      setBuildingUnitsLoading(false)
    }
  }

  const handlePrimaryPickerSelect = uprn => {
    setPrimaryPickerOpen(false)
    handlePrimaryUprnSelect(uprn)
  }

  const handleMapSelectionChange = async selection => {
    const nextSelection = {
      toids: selection.toids || [],
      uprns: selection.uprns || [],
      features: selection.features || [],
      links: selection.links || []
    }
    const primaryUprn = nextSelection.uprns[0] || ''

    if (
      trustedBuildingRef.current.toids?.join(',') !==
      nextSelection.toids.join(',')
    ) {
      trustedBuildingRef.current = { uprn: '', childUprns: [] }
      setBuildingUnits(null)
    }
    const keptUnitUprns = trustedBuildingRef.current.unitUprns
    const selectedUnitUprns = keptUnitUprns?.length
      ? keptUnitUprns
      : nextSelection.uprns

    // Set in the same render as the outline so its full unit count never flashes before the building's own.
    const selectedBlock = resolveBlockOfFlatsReference(selection.referenceDetails)
    if (
      selection.source === 'postcode-autoselect' &&
      selectedBlock.isBlockOfFlats &&
      outlineHasOtherUnits({ ...selectedBlock, mapUprns: nextSelection.uprns })
    ) {
      setBuildingUnitsLoading(true)
    }
    setMapSelection(nextSelection)
    if (nextSelection.toids.length) {
      setMapSearchLoading(false)
    }
    setPrimaryPickerOpen(false)
    setBp(prev => ({
      ...prev,
      selectedMapToids: nextSelection.toids,
      selectedMapUprns: selectedUnitUprns,
      selectedMapFeatures: nextSelection.features,
      selectedOsLinkedIdentifiers: nextSelection.links,
      suggestedUnitsCount: selectedUnitUprns.length || prev.suggestedUnitsCount
    }))

    if (!primaryUprn) {
      if (nextSelection.toids.length) {
        setReferenceLookupMessage({
          type: 'warning',
          text: 'The selected OS building did not return a UPRN yet. Try another building outline or use the address search above.'
        })
      }
      return
    }

    if (selection.source === 'postcode-autoselect') {
      const searchedPostcode =
        String(
          selection.searchedPostcode || sectionValues.postalCode || ''
        ).trim() || sectionValues.postalCode
      const usedFallback = selection.hintSource === 'postcoder-fallback'
      const referenceDetails = selection.referenceDetails || {}
      const {
        isBlockOfFlats,
        buildingUprn,
        childUprns: buildingChildUprns
      } = resolveBlockOfFlatsReference(referenceDetails)

      trustedBuildingRef.current = isBlockOfFlats
        ? {
            uprn: buildingUprn,
            childUprns: buildingChildUprns,
            childClassificationCode: referenceDetails.classificationCode || '',
            toids: nextSelection.toids
          }
        : { uprn: '', childUprns: [] }

      const referenceUprn =
        buildingUprn || String(referenceDetails.uprn || '').trim() || primaryUprn
      const referenceName = referenceDetails.name || ''
      const referenceStreet =
        referenceDetails.street || referenceDetails.address || ''
      const referenceCity = referenceDetails.city || ''
      const referenceBuildingTypes = Array.isArray(
        referenceDetails.buildingTypes
      )
        ? referenceDetails.buildingTypes
        : []
      const hasReferenceAddressDetails =
        Boolean(referenceName) ||
        Boolean(referenceStreet) ||
        Boolean(referenceCity)
      const hasReferenceTypeDetails = referenceBuildingTypes.length > 0

      setSelectionLocked(true)
      setManualEntryEnabled(true)
      setSectionValues(prev => ({
        ...prev,
        uprn: referenceUprn,
        name: referenceName || prev.name,
        street: referenceStreet || prev.street,
        city: referenceCity || prev.city,
        postalCode: referenceDetails.postalCode || searchedPostcode
      }))
      setBp(prev => ({
        ...prev,
        uprn: referenceUprn,
        name: referenceName || prev.name,
        street: referenceStreet || prev.street,
        city: referenceCity || prev.city,
        postalCode: referenceDetails.postalCode || searchedPostcode,
        buildingTypes: referenceBuildingTypes.length
          ? referenceBuildingTypes
          : prev.buildingTypes,
        suggestedUnitsCount:
          nextSelection.uprns.length || prev.suggestedUnitsCount
      }))
      setReferenceLookupMessage({
        type: 'success',
        text: usedFallback
          ? `Selected the OS building linked to ${searchedPostcode}. Local postcode data did not have this postcode, so Postcoder was used only as a fallback to find the matching UPRN.`
          : `Selected the OS building linked to ${searchedPostcode} using local UPRN data. Review the address fields, then continue.`
      })

      const needsBuildingUnits =
        isBlockOfFlats &&
        outlineHasOtherUnits({
          buildingUprn,
          childUprns: buildingChildUprns,
          mapUprns: nextSelection.uprns
        })
      const buildingUnitUprns = needsBuildingUnits
        ? await loadBuildingUnits({
            buildingUprn,
            name: referenceName,
            street: referenceStreet,
            city: referenceCity,
            postcodeChildUprns: buildingChildUprns,
            mapUprns: nextSelection.uprns
          })
        : null

      if (!buildingUnitUprns) {
        setBuildingUnits(null)
      }

      if (!hasReferenceAddressDetails || !hasReferenceTypeDetails) {
        const identityUprn = isBlockOfFlats ? buildingUprn : primaryUprn
        const detailsUprn = isBlockOfFlats
          ? buildingChildUprns[0] || buildingUprn
          : primaryUprn

        await lookupBuildingDetails({
          uprn: detailsUprn,
          seedCandidate: {
            ...buildSeedCandidate(
              identityUprn,
              nextSelection.toids[0] || '',
              buildingUnitUprns?.length || nextSelection.uprns.length
            ),
            postalCode: searchedPostcode
          }
        })
      }

      return
    }

    if (
      String(primaryUprn) === String(sectionValues.uprn || '') ||
      trustedBuildingRef.current.uprn
    ) {
      return
    }

    const primaryToid = nextSelection.toids[0] || ''

    await lookupBuildingDetails({
      uprn: primaryUprn,
      seedCandidate: buildSeedCandidate(
        primaryUprn,
        primaryToid,
        nextSelection.uprns.length
      )
    })
  }

  const clearBuildingSelection = preservedPostcode => {
    trustedBuildingRef.current = { uprn: '', childUprns: [] }
    setBuildingUnits(null)
    setBuildingUnitsLoading(false)
    setSelectionLocked(false)
    setReferenceLookupMessage(null)
    setManualEntryEnabled(false)
    setMapSelection({
      toids: [],
      uprns: [],
      features: [],
      links: []
    })
    setSectionValues(prev => ({
      ...prev,
      uprn: '',
      name: '',
      street: '',
      city: '',
      postalCode: preservedPostcode
    }))
    setBp(prev => ({
      ...prev,
      uprn: '',
      name: '',
      street: '',
      city: '',
      postalCode: preservedPostcode,
      referenceData: null,
      buildingData: null,
      suggestedBuildingType: null,
      suggestedUnitsCount: null,
      floorsNumber: null,
      undergroundFloorsNumber: null,
      buildingTypes: [],
      selectedMapToids: [],
      selectedMapUprns: [],
      selectedMapFeatures: [],
      selectedOsLinkedIdentifiers: []
    }))
    setAddressSearch(preservedPostcode)
  }

  const normalizePostcode = value =>
    String(value || '')
      .replace(/\s+/g, '')
      .toUpperCase()

  const validMapCoordinates = points =>
    points
      .map(point => [Number(point.longitude), Number(point.latitude)])
      .filter(
        coordinate =>
          Number.isFinite(coordinate[0]) && Number.isFinite(coordinate[1])
      )

  const candidateUprns = candidates =>
    candidates
      .map(candidate => String(candidate?.uprn || '').trim())
      .filter(Boolean)

  const candidateToids = points =>
    points
      .flatMap(point => [
        ...(Array.isArray(point?.topographicAreaToids)
          ? point.topographicAreaToids
          : []),
        point?.topographicAreaToid,
        point?.topography_layer_toid,
        point?.toid
      ])
      .map(toid => String(toid || '').trim())
      .filter(Boolean)

  const handleMapSearchSubmit = async event => {
    event?.preventDefault?.()
    event?.stopPropagation?.()

    const query = String(addressSearch || sectionValues.postalCode || '').trim()

    if (!query) {
      return
    }

    setMapSearchLoading(true)
    setReferenceLookupMessage(null)

    if (
      normalizePostcode(query) !== normalizePostcode(sectionValues.postalCode)
    ) {
      clearBuildingSelection(query)
    } else {
      setAddressSearch(query)
      setSectionValues(prev => ({ ...prev, postalCode: query }))
    }

    const shouldFocusSelectedBuilding =
      mapSelection.features.length > 0 &&
      normalizePostcode(query) === normalizePostcode(sectionValues.postalCode)

    let coordinates = []
    let targetUprns = []
    let targetToids = []
    let referenceDetails = null
    let hintSource = 'local'

    if (!shouldFocusSelectedBuilding) {
      try {
        const { data } = await bpService.getPostcodeUprnHints({
          postcode: query,
          uprn: String(sectionValues.uprn || bp?.uprn || '').trim() || undefined
        })
        const localPoints = data?.points || []

        targetUprns = data?.uprns || []
        targetToids = data?.toids || candidateToids(data?.points || [])
        referenceDetails = data?.reference || null
        coordinates = validMapCoordinates(localPoints)
      } catch (error) {
        coordinates = []
        targetUprns = []
        targetToids = []
      }

      const needsReferenceFallback = !coordinates.length || !targetToids.length

      if (needsReferenceFallback) {
        try {
          const { data } = await bpService.lookupBuildingReference({
            postcode: query,
            list: true
          })
          const candidates = data?.candidates || []
          const candidateCoordinates = validMapCoordinates(candidates)
          const candidateTargetUprns = candidateUprns(candidates)
          const candidateTargetToids = candidateToids(candidates)

          if (candidateCoordinates.length) {
            coordinates = candidateCoordinates
          }
          if (!targetUprns.length && candidateTargetUprns.length) {
            targetUprns = candidateTargetUprns
          }
          if (!targetToids.length && candidateTargetToids.length) {
            targetToids = candidateTargetToids
          }
          referenceDetails =
            pickReferenceCandidate(candidates) || referenceDetails
          hintSource = candidateTargetUprns.length
            ? 'postcoder-fallback'
            : hintSource
        } catch (error) {
          // Keep the map postcode search usable even if the paid fallback fails.
        }
      }
    }

    setMapFocusRequest({
      id: Date.now(),
      query,
      features: shouldFocusSelectedBuilding ? mapSelection.features : [],
      coordinates,
      targetUprns,
      targetToids,
      referenceDetails,
      hintSource
    })
  }

  const handleChangeBuilding = () => {
    setPrimaryPickerOpen(false)
    clearBuildingSelection(sectionValues.postalCode)
  }

  const handleUnavailableMapSelection = () => {
    if (!mapSelection.toids.length) {
      return
    }

    setEstatePrompt({
      open: true,
      mode: 'notice',
      reason: 'unavailable-selection'
    })
  }

  useEffect(() => {
    if (!primaryPickerOpen) {
      return undefined
    }

    const handlePointerDown = event => {
      if (!primaryPickerRef.current?.contains(event.target)) {
        setPrimaryPickerOpen(false)
      }
    }

    document.addEventListener('mousedown', handlePointerDown, true)

    return () => {
      document.removeEventListener('mousedown', handlePointerDown, true)
    }
  }, [primaryPickerOpen])

  useEffect(() => {
    const nextSectionValues = {
      name: propOr('', 'name', bp),
      uprn: propOr('', 'uprn', bp),
      postalCode: propOr('', 'postalCode', bp),
      street: propOr('', 'street', bp),
      city: propOr('', 'city', bp),
      expectedUprnAssignmentDate: propOr(
        null,
        'expectedUprnAssignmentDate',
        bp
      ),
      groundFloor: propOr(true, 'groundFloor', bp)
    }

    setSectionValues(prev =>
      equals(prev, nextSectionValues) ? prev : nextSectionValues
    )
    setSelectionLocked(Boolean(propOr('', 'uprn', bp)))
    setEntryMode(prev => prev || initialEntryMode(bp))
    setAddressSearch(prev => nextSectionValues.postalCode || prev)
    setMapSelection({
      toids: propOr([], 'selectedMapToids', bp),
      uprns: propOr([], 'selectedOsLinkedIdentifiers', bp)?.length
        ? Array.from(
            new Set(
              propOr([], 'selectedOsLinkedIdentifiers', bp)
                .flatMap(link => link?.uprns || [])
                .map(value => String(value || '').trim())
                .filter(Boolean)
            )
          )
        : propOr([], 'selectedMapUprns', bp),
      features: propOr([], 'selectedMapFeatures', bp),
      links: propOr([], 'selectedOsLinkedIdentifiers', bp)
    })
  }, [bp])

  const commitBuildingInformation = () => {
    const committedBuildingUnits =
      buildingUnits?.buildingUprn === String(sectionValues.uprn || '').trim()
        ? buildingUnits.uprns
        : null
    const osMapSelectionPayload = {
      selectedMapToids: mapSelection.toids,
      selectedMapUprns: committedBuildingUnits || mapSelection.uprns,
      selectedMapFeatures: mapSelection.features,
      selectedOsLinkedIdentifiers: mapSelection.links
    }

    setActiveStep('building-details')
    setBp(prev => ({
      ...prev,
      ...sectionValues,
      ...osMapSelectionPayload
    }))
    setCompleted('buildingInfo')

    if (isNotNilOrEmpty(resolvedBpId)) {
      dispatch(
        updateBpRoutine({
          ...sectionValues,
          ...osMapSelectionPayload,
          id: resolvedBpId,
          isEdit: true,
          isWizard: true
        })
      )
    }
  }

  const setNextSection = () => {
    commitBuildingInformation()
  }

  const startManualEntry = () => {
    setManualEntryEnabled(true)
    setSectionValues(prev => manualEntryValues(prev, addressSearch))
    setEntryMode(ENTRY_MODE_MANUAL)
  }

  // The map result is not right for this building: keep the address fields and leave the units to a later sync.
  const fillUnitDataManually = () => {
    const nextValues = manualEntryValues(sectionValues, addressSearch)

    trustedBuildingRef.current = { uprn: '', childUprns: [] }
    setBuildingUnits(null)
    setBuildingUnitsLoading(false)
    setPrimaryPickerOpen(false)
    setReferenceLookupMessage(null)
    setManualEntryEnabled(true)
    setMapSelection(EMPTY_MAP_SELECTION)
    setSectionValues(nextValues)
    setBp(prev => ({ ...prev, ...manualEntryBpPatch(nextValues) }))
    setEntryMode(ENTRY_MODE_MANUAL)
  }

  const useMapInstead = () => {
    setManualEntryEnabled(false)
    setAddressSearch(prev => sectionValues.postalCode || prev)
    setEntryMode(ENTRY_MODE_MAP)
  }

  const handleEstatePromptSubmit = () => {
    closeEstatePrompt()
    commitBuildingInformation()
  }

  useEffect(() => {
    validateBuildingInformationWithoutUprnValues(sectionValues, setIsValid)
  }, [sectionValues])

  const areApiFieldsDisabled = !manualEntryEnabled
  const showReferenceSkeleton =
    (referenceLookupLoading || buildingUnitsLoading) &&
    mapSelection.toids.length > 0
  const canContinueBuildingInfo =
    (manualEntryEnabled || isNotNilOrEmpty(sectionValues.uprn)) &&
    valid &&
    !referenceLookupLoading
  const primaryUprn = String(sectionValues.uprn || '').trim()
  const activeBuildingUnits =
    buildingUnits?.buildingUprn === primaryUprn ? buildingUnits.uprns : null
  const displayedUprns = activeBuildingUnits || mapSelection.uprns
  const normalizedLinkedUprns = Array.from(
    new Set(
      [...(activeBuildingUnits ? [primaryUprn] : []), ...displayedUprns]
        .map(value => String(value || '').trim())
        .filter(Boolean)
    )
  )
  const unitReviewUprns = normalizedLinkedUprns.filter(
    uprn => !primaryUprn || uprn !== primaryUprn
  )
  const primaryAddressTitle =
    sectionValues.name ||
    sectionValues.street ||
    (primaryUprn ? 'Selected primary address' : 'No primary UPRN selected')
  const primaryAddressSubtitle = [
    sectionValues.street,
    sectionValues.city,
    sectionValues.postalCode
  ]
    .filter(Boolean)
    .join(', ')

  return (
    <Panel
      open={open}
      title='Building information'
      completed={completed}
      step='building-info'
      setActiveStep={setActiveStep}
      fullWidth
    >
      {!entryMode && (
        <BuildingEntryChoice
          onNewBuild={startManualEntry}
          onExistingBuilding={() => setEntryMode(ENTRY_MODE_MAP)}
        />
      )}

      {entryMode === ENTRY_MODE_MANUAL && (
        <ManualBuildingEntry
          values={sectionValues}
          onChange={setValues}
          onConfirm={setNextSection}
          onUseMap={useMapInstead}
          canContinue={valid}
          completed={completed}
        />
      )}

      {entryMode === ENTRY_MODE_MAP && (
        <MapFirstWorkspace>
          <WorkspaceHero>
            <HeroCopy>
              <HeroEyebrow>OS map selection</HeroEyebrow>
              <HeroTitle>Locate the building, then click its outline</HeroTitle>
              <HeroText>
                Use the postcode to jump to the right area. The Building Passport
                is created from the OS geometry you select on the map.
              </HeroText>
            </HeroCopy>

            <SearchBlock>
              <MapSearchInputWrap>
                <SearchInputIcon />
                <MapSearchInput
                  value={addressSearch}
                  name='addressSearch'
                  placeholder='Enter postcode'
                  disabled={referenceLookupLoading || mapSearchLoading}
                  autoComplete='postal-code'
                  onChange={event => {
                    setReferenceLookupMessage(null)
                    setAddressSearch(event.target.value)
                  }}
                  onKeyDown={event => {
                    if (event.key === 'Enter') {
                      handleMapSearchSubmit(event)
                    }
                  }}
                />
              </MapSearchInputWrap>
              <MapSearchButton
                type='button'
                onClick={handleMapSearchSubmit}
                disabled={
                  referenceLookupLoading ||
                  mapSearchLoading ||
                  !String(addressSearch).trim()
                }
              >
                <PlaceOutlinedIcon />
                Show on map
              </MapSearchButton>
            </SearchBlock>
          </WorkspaceHero>

          <MapWorkspaceGrid>
            <MapCanvasColumn>
              <OsBuildingMultiSelectMap
                selectedToids={mapSelection.toids}
                selectedUprns={mapSelection.uprns}
                selectedFeatures={mapSelection.features}
                focusRequest={mapFocusRequest}
                postcodeSearchLoading={mapSearchLoading}
                onSelectionChange={handleMapSelectionChange}
                onUnavailableSelection={handleUnavailableMapSelection}
                onFocusSettled={() => setMapSearchLoading(false)}
                disabled={referenceLookupLoading}
              />
            </MapCanvasColumn>

            <SelectionInspector>
              <InspectorPanel>
                <InspectorHeader>
                  <InspectorIcon>
                    <ApartmentOutlinedIcon />
                  </InspectorIcon>
                  <div>
                    <InspectorEyebrow>Current selection</InspectorEyebrow>
                    <InspectorTitle>{currentSelectionTitle}</InspectorTitle>
                  </div>
                </InspectorHeader>

                <SelectionStats>
                  <strong>{mapSelection.toids.length}</strong> building
                  {mapSelection.toids.length === 1 ? '' : 's'} selected
                  <span />
                  {buildingUnitsLoading ? (
                    <>
                      <FieldSkeleton variant='rounded' width={28} height={16} />{' '}
                      units found
                    </>
                  ) : (
                    <>
                      <strong>{displayedUprns.length}</strong> total unit
                      {displayedUprns.length === 1 ? '' : 's'} found
                    </>
                  )}
                </SelectionStats>

                {buildingUnitsLoading && (
                  <FieldSkeleton variant='rounded' width='100%' height={140} />
                )}

                {!buildingUnitsLoading && displayedUprns.length > 0 && (
                  <UprnSelectionPanel>
                    <UprnSelectionHeader>
                      <div>
                        <UprnSelectionTitle>
                          Choose primary address
                        </UprnSelectionTitle>
                        <UprnSelectionHelp>
                          {activeBuildingUnits
                            ? `${displayedUprns.length} unit${
                                displayedUprns.length === 1 ? '' : 's'
                              } belong to this building (${
                                mapSelection.uprns.length
                              } on the OS outline, which also covers neighbouring buildings).`
                            : `${displayedUprns.length} total linked unit${
                                displayedUprns.length === 1 ? '' : 's'
                              } found.`}{' '}
                          Choose the primary address for this Building Passport.
                        </UprnSelectionHelp>
                      </div>
                      <UprnSelectionMeta>
                        {unitReviewUprns.length} linked unit
                        {unitReviewUprns.length === 1 ? '' : 's'}
                      </UprnSelectionMeta>
                    </UprnSelectionHeader>

                    <UprnSectionLabel>Primary address</UprnSectionLabel>
                    <PrimaryUprnCard
                      ref={primaryPickerRef}
                      role='button'
                      tabIndex={referenceLookupLoading ? -1 : 0}
                      aria-haspopup='listbox'
                      aria-expanded={primaryPickerOpen}
                      onClick={() =>
                        !referenceLookupLoading &&
                        setPrimaryPickerOpen(prev => !prev)
                      }
                      onKeyDown={event => {
                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault()
                          if (!referenceLookupLoading) {
                            setPrimaryPickerOpen(prev => !prev)
                          }
                        }
                        if (event.key === 'Escape') {
                          setPrimaryPickerOpen(false)
                        }
                      }}
                    >
                      {primaryPickerOpen && (
                        <PrimaryDropdown role='listbox'>
                          {normalizedLinkedUprns.map(uprn => {
                            const isSelected =
                              String(primaryUprn || mapSelection.uprns[0]) ===
                              uprn

                            return (
                              <PrimaryDropdownOption
                                type='button'
                                key={uprn}
                                $selected={isSelected}
                                role='option'
                                aria-selected={isSelected}
                                onClick={event => {
                                  event.stopPropagation()
                                  handlePrimaryPickerSelect(uprn)
                                }}
                              >
                                <DropdownCheck>
                                  {isSelected ? '✓' : ''}
                                </DropdownCheck>
                                <DropdownIcon>
                                  <ApartmentOutlinedIcon />
                                </DropdownIcon>
                                <DropdownOptionText>
                                  UPRN {uprn}
                                </DropdownOptionText>
                              </PrimaryDropdownOption>
                            )
                          })}
                        </PrimaryDropdown>
                      )}
                      <UprnCardIcon>
                        <ApartmentOutlinedIcon />
                      </UprnCardIcon>
                      <PrimaryUprnContent>
                        <PrimaryUprnTitle>{primaryAddressTitle}</PrimaryUprnTitle>
                        <PrimaryUprnMeta>
                          UPRN {primaryUprn || mapSelection.uprns[0]}
                        </PrimaryUprnMeta>
                        {primaryAddressSubtitle && (
                          <PrimaryUprnAddress>
                            {primaryAddressSubtitle}
                          </PrimaryUprnAddress>
                        )}
                      </PrimaryUprnContent>
                      <PrimarySelectCue $open={primaryPickerOpen}>
                        <span>Primary</span>
                      </PrimarySelectCue>
                    </PrimaryUprnCard>
                  </UprnSelectionPanel>
                )}

                {referenceLookupLoading && (
                  <StatusPill type='loading'>
                    <CircularProgress size={14} />
                    Looking up building reference...
                  </StatusPill>
                )}

                {referenceLookupMessage && (
                  <StatusPill type={referenceLookupMessage.type}>
                    {referenceLookupMessage.text}
                  </StatusPill>
                )}

                {!mapSelection.toids.length && (
                  <EmptySelectionText>
                    Click a building outline on the map to resolve linked UPRNs.
                  </EmptySelectionText>
                )}

                <SelectionActions>
                  {(selectionLocked || mapSelection.toids.length > 0) && (
                    <SecondaryActionButton
                      type='button'
                      onClick={handleChangeBuilding}
                    >
                      Clear selected building
                    </SecondaryActionButton>
                  )}

                  <SecondaryActionButton
                    type='button'
                    onClick={fillUnitDataManually}
                    disabled={referenceLookupLoading || buildingUnitsLoading}
                  >
                    Manually fill unit data
                  </SecondaryActionButton>
                </SelectionActions>

                <ReviewFieldsHeader>Review fields</ReviewFieldsHeader>
                <ReviewField>
                  <ReviewLabel>{t('createBp.labels.uprn')}</ReviewLabel>
                  <ReviewValue>
                    {showReferenceSkeleton ? (
                      <FieldSkeleton variant='rounded' width='56%' height={16} />
                    ) : (
                      sectionValues.uprn || 'Waiting for map selection'
                    )}
                  </ReviewValue>
                </ReviewField>

                <ReviewField>
                  <ReviewLabel>{t('createBp.labels.name')}</ReviewLabel>
                  {showReferenceSkeleton ? (
                    <ReviewInputSkeleton>
                      <FieldSkeleton variant='rounded' width='68%' height={16} />
                    </ReviewInputSkeleton>
                  ) : (
                    <ReviewInput
                      value={sectionValues.name}
                      placeholder='Building name or number'
                      onChange={event => setValues('name', event.target.value)}
                    />
                  )}
                </ReviewField>

                <ReviewField>
                  <ReviewLabel>{t('createBp.labels.address')}</ReviewLabel>
                  {showReferenceSkeleton ? (
                    <ReviewInputSkeleton>
                      <FieldSkeleton variant='rounded' width='78%' height={16} />
                    </ReviewInputSkeleton>
                  ) : (
                    <ReviewInput
                      value={sectionValues.street}
                      placeholder='Address'
                      disabled={areApiFieldsDisabled}
                      onChange={event => setValues('street', event.target.value)}
                    />
                  )}
                </ReviewField>

                <ReviewField>
                  <ReviewLabel>{t('createBp.labels.city')}</ReviewLabel>
                  {showReferenceSkeleton ? (
                    <ReviewInputSkeleton>
                      <FieldSkeleton variant='rounded' width='42%' height={16} />
                    </ReviewInputSkeleton>
                  ) : (
                    <ReviewInput
                      value={sectionValues.city}
                      placeholder='City'
                      disabled={areApiFieldsDisabled}
                      onChange={event => setValues('city', event.target.value)}
                    />
                  )}
                </ReviewField>

                {mapSelection.toids.length > 0 && (
                  <CadastralDetails>
                    <CadastralSummary>More OS details</CadastralSummary>
                    <ReviewField>
                      <ReviewLabel>OS TOID</ReviewLabel>
                      <ReviewValue>{mapSelection.toids.join(', ')}</ReviewValue>
                    </ReviewField>
                    <ReviewField>
                      <ReviewLabel>Linked UPRNs</ReviewLabel>
                      <ReviewValue>
                        {displayedUprns.length
                          ? displayedUprns.join(', ')
                          : 'Waiting for linked identifiers'}
                      </ReviewValue>
                    </ReviewField>
                  </CadastralDetails>
                )}

                {(isNotNilOrEmpty(sectionValues.uprn) ||
                  isNotNilOrEmpty(sectionValues.postalCode)) && (
                  <PublicDataNamesOption data-tour-id='createBp.use-public-data-names'>
                    <Checkbox
                      value={Boolean(sectionValues.usePublicDataUnitNames)}
                      label='Use unit names from the public address data'
                      name='usePublicDataUnitNames'
                      onChange={(name, checked) => setValues(name, Boolean(checked))}
                    />
                    <PublicDataNamesHint>
                      When you sync units, the apartment names from the official OS
                      address data are used straight away instead of the default
                      unit names. It is only a setting and runs no address lookups.
                    </PublicDataNamesHint>
                  </PublicDataNamesOption>
                )}

                <PrimaryActionButton
                  type='button'
                  onClick={setNextSection}
                  disabled={!canContinueBuildingInfo}
                >
                  <CheckCircleOutlineOutlinedIcon />
                  {completed ? 'Save changes' : 'Confirm building'}
                </PrimaryActionButton>
                <InspectorHint>
                  Linked units can be synced after this Building Passport is
                  activated.
                </InspectorHint>
              </InspectorPanel>
            </SelectionInspector>
          </MapWorkspaceGrid>
        </MapFirstWorkspace>
      )}

      <Modal
        open={lookupModal.open}
        onClose={closeLookupModal}
        onCancel={closeLookupModal}
        onSubmit={closeLookupModal}
        title={lookupModal.title}
        withCancel={false}
        withSubmit
        submitButtonText='Continue'
      >
        <ModalText>{lookupModal.message}</ModalText>
      </Modal>

      <Modal
        open={estatePrompt.open}
        onClose={closeEstatePrompt}
        onCancel={closeEstatePrompt}
        onSubmit={handleEstatePromptSubmit}
        title='Create an estate for this split building'
        withCancel
        withSubmit
        cancelButtonText='Review selection'
        submitButtonText='Continue this BP'
        disabled={!canContinueBuildingInfo}
      >
        <ModalText>
          That part of the map cannot be added to the current Building Passport
          as another OS building selection. Keep the selected outline as this
          BP, then create the remaining part as a separate BP and add both BPs
          to an estate.
        </ModalText>
        <EstateModalSummary>
          An estate groups one or more Building Passports that should be managed
          together after each BP is created.
        </EstateModalSummary>
      </Modal>
    </Panel>
  )
}

const buildLookupSuccessMessage = (source = 'postcode') => {
  const sourceLabel =
    source === 'uprn'
      ? 'UPRN'
      : source === 'map'
        ? 'OS map building'
        : source === 'autocomplete'
          ? 'address'
          : 'postcode'

  return `Building information was filled automatically from the selected ${sourceLabel}. When the address classification is available, we will suggest the main building type in the next steps.`
}

export default BuildingInformation

const MapFirstWorkspace = styled.div`
  display: flex;
  flex-direction: column;
  gap: 18px;
`

const WorkspaceHero = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 0.95fr) minmax(360px, 0.75fr);
  gap: 24px;
  align-items: end;
  padding: 0 0 18px;
  border-bottom: 1px solid rgba(12, 18, 41, 0.08);

  @media (max-width: 980px) {
    grid-template-columns: 1fr;
  }
`

const HeroCopy = styled.div`
  max-width: 720px;
`

const HeroEyebrow = styled.div`
  margin-bottom: 8px;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
`

const HeroTitle = styled.h2`
  margin: 0;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 28px;
  font-weight: 700;
  line-height: 1.12;
`

const HeroText = styled.p`
  max-width: 620px;
  margin: 10px 0 0;
  color: rgba(12, 18, 41, 0.68);
  font-size: 14px;
  font-weight: 500;
  line-height: 1.55;
`

const SearchBlock = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  align-items: center;
  min-width: 0;

  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`

const MapSearchInputWrap = styled.div`
  position: relative;
  min-width: 0;
`

const SearchInputIcon = styled(SearchOutlinedIcon)`
  && {
    position: absolute;
    top: 50%;
    left: 14px;
    width: 19px;
    height: 19px;
    color: rgba(12, 18, 41, 0.42);
    transform: translateY(-50%);
    pointer-events: none;
  }
`

const MapSearchInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  height: 52px;
  border: 1px solid rgba(12, 18, 41, 0.14);
  border-radius: 8px;
  background: #ffffff;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 15px;
  font-weight: 600;
  outline: none;
  padding: 0 14px 0 44px;
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease;

  &::placeholder {
    color: rgba(12, 18, 41, 0.38);
    font-weight: 500;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.14);
  }
`

const MapSearchButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 7px;
  min-height: 52px;
  min-width: 148px;
  border: 0;
  border-radius: 8px;
  padding: 0 17px;
  background: ${({ theme }) => theme.colors.haiti};
  color: ${({ theme }) => theme.colors.white};
  font-size: 13px;
  font-weight: 700;
  white-space: nowrap;
  cursor: pointer;
  transition:
    opacity 140ms ease,
    transform 140ms ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`

const MapWorkspaceGrid = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(320px, 380px);
  gap: 20px;
  align-items: start;
  min-width: 0;

  @media (max-width: 1180px) {
    grid-template-columns: 1fr;
  }
`

const MapCanvasColumn = styled.div`
  min-width: 0;
`

const SelectionInspector = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;

  @media (min-width: 1181px) {
    position: sticky;
    top: 88px;
  }
`

const InspectorPanel = styled.aside`
  box-sizing: border-box;
  min-width: 0;
  max-width: 100%;
  border: 1px solid rgba(12, 18, 41, 0.1);
  border-radius: 8px;
  background: #ffffff;
  box-shadow: 0 14px 34px rgba(12, 18, 41, 0.06);
  padding: 18px;
`

const InspectorHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 14px;
`

const InspectorIcon = styled.div`
  flex: 0 0 auto;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 8px;
  background: rgba(51, 190, 204, 0.12);
  color: ${({ theme }) => theme.colors.secondary};

  svg {
    width: 21px;
    height: 21px;
  }
`

const InspectorEyebrow = styled.div`
  color: rgba(12, 18, 41, 0.46);
  font-size: 10px;
  font-weight: 800;
  letter-spacing: 0.08em;
  line-height: 1.25;
  text-transform: uppercase;
`

const InspectorTitle = styled.div`
  margin-top: 4px;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 16px;
  font-weight: 800;
  line-height: 1.35;
`

const SelectionStats = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin: -4px 0 14px 52px;
  color: rgba(12, 18, 41, 0.58);
  font-size: 12px;
  font-weight: 700;
  line-height: 1.35;

  strong {
    color: ${({ theme }) => theme.colors.haiti};
    font-size: 15px;
    font-weight: 800;
  }

  span {
    width: 1px;
    height: 14px;
    background: rgba(12, 18, 41, 0.14);
  }
`

const StatusPill = styled.div`
  box-sizing: border-box;
  display: inline-flex;
  align-items: flex-start;
  gap: 7px;
  width: 100%;
  max-width: 100%;
  min-width: 0;
  border-radius: 6px;
  border: 0;
  border-left: 3px solid
    ${({ type, theme }) => {
      if (type === 'success') return '#247a38'
      if (type === 'warning') return '#8a6d1d'
      if (type === 'error') return theme.colors.error
      return theme.colors.secondary
    }};
  background: ${({ type }) => {
    if (type === 'success') return 'rgba(36, 122, 56, 0.08)'
    if (type === 'warning') return 'rgba(138, 109, 29, 0.08)'
    if (type === 'error') return 'rgba(218, 63, 48, 0.08)'
    return 'rgba(51, 190, 204, 0.08)'
  }};
  color: ${({ type, theme }) => {
    if (type === 'success') return '#247a38'
    if (type === 'warning') return '#7a5c12'
    if (type === 'error') return theme.colors.error
    return theme.colors.secondary
  }};
  font-size: 12px;
  font-weight: 700;
  line-height: 1.45;
  overflow-wrap: anywhere;
  padding: 10px 12px;
  margin-bottom: 12px;
`

const UprnSelectionPanel = styled.div`
  box-sizing: border-box;
  width: 100%;
  border: 0;
  background: transparent;
  padding: 0;
  margin: 0 0 18px;
`

const UprnSelectionHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 16px;
`

const UprnSelectionTitle = styled.div`
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 16px;
  font-weight: 800;
  line-height: 1.25;
`

const UprnSelectionMeta = styled.div`
  flex: 0 0 auto;
  border-radius: 8px;
  background: rgba(51, 190, 204, 0.13);
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 12px;
  font-weight: 800;
  line-height: 1;
  padding: 8px 10px;
  white-space: nowrap;
`

const UprnSelectionHelp = styled.div`
  color: rgba(12, 18, 41, 0.58);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  margin-top: 5px;
  max-width: 330px;
`

const UprnSectionLabel = styled.div`
  color: rgba(12, 18, 41, 0.52);
  font-size: 11px;
  font-weight: 800;
  line-height: 1.25;
  margin-bottom: 8px;
`

const PrimaryUprnCard = styled.div`
  box-sizing: border-box;
  position: relative;
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  width: 100%;
  border: 2px solid ${({ theme }) => theme.colors.secondary};
  border-radius: 8px;
  background: #f8fcfd;
  padding: 13px 14px;
  margin-bottom: 0;
  cursor: pointer;

  &:focus {
    outline: none;
  }

  &:focus-within {
    box-shadow: 0 0 0 4px rgba(51, 190, 204, 0.18);
  }
`

const UprnCardIcon = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 34px;
  height: 34px;
  border-radius: 8px;
  background: rgba(51, 190, 204, 0.13);
  color: ${({ theme }) => theme.colors.secondary};

  svg {
    width: 20px;
    height: 20px;
  }
`

const PrimaryUprnContent = styled.div`
  min-width: 0;
`

const PrimaryUprnTitle = styled.div`
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 14px;
  font-weight: 800;
  line-height: 1.3;
  overflow-wrap: anywhere;
`

const PrimaryUprnMeta = styled.div`
  color: rgba(12, 18, 41, 0.52);
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  margin-top: 2px;
  overflow-wrap: anywhere;
`

const PrimaryUprnAddress = styled.div`
  color: rgba(12, 18, 41, 0.48);
  font-size: 11px;
  font-weight: 600;
  line-height: 1.35;
  margin-top: 2px;
  overflow-wrap: anywhere;
`

const PrimarySelectCue = styled.div`
  position: relative;
  flex: 0 0 auto;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 999px;
  background: rgba(51, 190, 204, 0.14);
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 11px;
  font-weight: 800;
  line-height: 1;
  padding: 8px 22px 8px 10px;
  white-space: nowrap;
  pointer-events: none;

  &::after {
    content: '';
    position: absolute;
    right: 9px;
    top: 50%;
    width: 6px;
    height: 6px;
    border-right: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: translateY(${({ $open }) => ($open ? '-35%' : '-65%')})
      rotate(${({ $open }) => ($open ? '225deg' : '45deg')});
    pointer-events: none;
  }
`

const PrimaryDropdown = styled.div`
  position: absolute;
  top: calc(100% + 8px);
  left: 0;
  right: 0;
  z-index: 30;
  max-height: 320px;
  overflow-y: auto;
  border: 1px solid rgba(12, 18, 41, 0.12);
  border-radius: 8px;
  background: rgba(255, 255, 255, 0.98);
  box-shadow: 0 18px 42px rgba(12, 18, 41, 0.18);
  padding: 8px;
`

const PrimaryDropdownOption = styled.button`
  box-sizing: border-box;
  display: grid;
  grid-template-columns: 18px auto minmax(0, 1fr);
  align-items: center;
  gap: 9px;
  width: 100%;
  min-height: 40px;
  border: 0;
  border-radius: 7px;
  background: ${({ $selected, theme }) =>
    $selected ? theme.colors.secondary : 'transparent'};
  color: ${({ $selected, theme }) =>
    $selected ? theme.colors.white : theme.colors.haiti};
  cursor: pointer;
  font: inherit;
  padding: 8px 10px;
  text-align: left;

  &:hover {
    background: ${({ $selected, theme }) =>
      $selected ? theme.colors.secondary : 'rgba(12, 18, 41, 0.05)'};
  }
`

const DropdownCheck = styled.span`
  color: currentColor;
  font-size: 14px;
  font-weight: 900;
  line-height: 1;
`

const DropdownIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border-radius: 6px;
  background: rgba(51, 190, 204, 0.12);
  color: currentColor;

  svg {
    width: 16px;
    height: 16px;
  }
`

const DropdownOptionText = styled.span`
  min-width: 0;
  color: currentColor;
  font-size: 13px;
  font-weight: 800;
  line-height: 1.3;
  overflow-wrap: anywhere;
`

const SecondaryActionButton = styled.button`
  border: none;
  background: transparent;
  color: ${({ theme }) => theme.colors.secondary};
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  padding: 0;
  margin-bottom: 14px;
`

const SelectionActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 18px;
`

const EmptySelectionText = styled.div`
  color: rgba(12, 18, 41, 0.58);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.45;
  margin-bottom: 14px;
`

const ReviewFieldsHeader = styled(InspectorEyebrow)`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(12, 18, 41, 0.08);
`

const PrimaryActionButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 100%;
  min-height: 48px;
  margin-top: 18px;
  border: 0;
  border-radius: 8px;
  background: ${({ theme }) => theme.colors.secondary};
  color: ${({ theme }) => theme.colors.white};
  font-size: 14px;
  font-weight: 800;
  cursor: pointer;
  transition:
    opacity 140ms ease,
    transform 140ms ease;

  svg {
    width: 18px;
    height: 18px;
  }

  &:hover:not(:disabled) {
    transform: translateY(-1px);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.45;
  }
`

const PublicDataNamesOption = styled.div`
  margin-top: 18px;
  padding: 14px 16px;
  border: 1px solid rgba(12, 18, 41, 0.08);
  border-radius: 8px;
  background: rgba(12, 18, 41, 0.02);

  label {
    color: #0c1229;
    font-size: 13px;
    font-weight: 600;
    line-height: 16px;
  }
`

// Same 30px inset as the checkbox label, so the hint lines up with the text.
const PublicDataNamesHint = styled.div`
  margin-top: 4px;
  padding-left: 30px;
  color: rgba(12, 18, 41, 0.48);
  font-size: 12px;
  font-weight: 500;
  line-height: 1.45;
`

const InspectorHint = styled.div`
  color: rgba(12, 18, 41, 0.48);
  font-size: 12px;
  font-weight: 600;
  line-height: 1.45;
  text-align: center;
  margin-top: 10px;
`

const ReviewField = styled.label`
  display: block;
  margin-top: 13px;
`

const ReviewLabel = styled.div`
  margin-bottom: 6px;
  color: rgba(12, 18, 41, 0.52);
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
`

const ReviewValue = styled.div`
  display: flex;
  align-items: center;
  min-height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(12, 18, 41, 0.1);
  background: #f7fafc;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 13px;
  font-weight: 700;
  line-height: 1.35;
  padding: 10px 12px;
`

const ReviewInputSkeleton = styled.div`
  box-sizing: border-box;
  display: flex;
  align-items: center;
  width: 100%;
  min-height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(12, 18, 41, 0.1);
  background: #f7fafc;
  padding: 0 12px;
`

const FieldSkeleton = styled(Skeleton)`
  && {
    transform: none;
    border-radius: 6px;
    background-color: rgba(12, 18, 41, 0.08);
  }
`

const ReviewInput = styled.input`
  box-sizing: border-box;
  width: 100%;
  min-height: 42px;
  border-radius: 8px;
  border: 1px solid rgba(12, 18, 41, 0.14);
  background: #ffffff;
  color: ${({ theme }) => theme.colors.haiti};
  font-size: 13px;
  font-weight: 700;
  outline: none;
  padding: 0 12px;
  transition:
    border-color 140ms ease,
    box-shadow 140ms ease;

  &::placeholder {
    color: rgba(12, 18, 41, 0.34);
    font-weight: 600;
  }

  &:focus {
    border-color: ${({ theme }) => theme.colors.secondary};
    box-shadow: 0 0 0 3px rgba(51, 190, 204, 0.14);
  }

  &:disabled {
    background: #f7fafc;
    color: rgba(12, 18, 41, 0.64);
    cursor: not-allowed;
  }
`

const CadastralDetails = styled.details`
  margin-top: 18px;
  padding-top: 16px;
  border-top: 1px solid rgba(12, 18, 41, 0.08);

  &[open] {
    padding-bottom: 4px;
  }

  ${ReviewValue} {
    overflow-wrap: anywhere;
    font-size: 12px;
  }
`

const CadastralSummary = styled.summary`
  color: ${({ theme }) => theme.colors.secondary};
  cursor: pointer;
  font-size: 12px;
  font-weight: 800;
  line-height: 1.35;
  list-style: none;

  &::-webkit-details-marker {
    display: none;
  }

  &::after {
    content: '+';
    margin-left: 8px;
    color: rgba(12, 18, 41, 0.48);
    font-weight: 800;
  }

  ${CadastralDetails}[open] &::after {
    content: '-';
  }
`

const ModalText = styled.div`
  font-size: 15px;
  line-height: 1.6;
  color: ${({ theme }) => theme.colors.haiti};
  margin-bottom: 16px;
`

const EstateModalSummary = styled.div`
  border-radius: 8px;
  background: rgba(51, 190, 204, 0.08);
  color: rgba(12, 18, 41, 0.72);
  font-size: 13px;
  font-weight: 600;
  line-height: 1.55;
  padding: 12px 14px;
`
